import React, { useMemo, useState } from 'react';
import { cn, formatDateSimple, extractPlainText } from '@/utils/formatter';
import { Badge } from '../Badge';
import { UserAvatar } from '../UserAvatar';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { Demanda } from '../../models';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { isPrazoAtrasado, isPrevisaoAtrasada } from '@/utils/helpers';

import iconLate from '../../assets/icons/IconLate.svg';

// Componentes base da tabela (reutilizáveis)
const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement> & {
  maxHeight?: string;
}>(({ className, maxHeight, ...props }, ref) => (
  <div 
    className="relative w-full overflow-y-auto overflow-x-auto"
    style={{ maxHeight: maxHeight || '60vh', paddingBottom: '8px' }}
  >
    <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <thead ref={ref} className={cn("[&_tr:first-child_th:first-child]:rounded-tl-lg [&_tr:first-child_th:last-child]:rounded-tr-lg", className)} {...props} />);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-0 [&_tr:last-child_td:first-child]:rounded-bl-lg [&_tr:last-child_td:last-child]:rounded-br-lg", className)} {...props} />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t border-border bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-gray-200 transition-colors data-[state=selected]:bg-gray-50",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-4 text-left align-middle font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-neutral-800 [&:has([role=checkbox])]:w-px [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-0.5",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-4 align-middle text-gray-900 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-0.5",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn("mt-4 text-sm text-gray-600", className)} {...props} />
));
TableCaption.displayName = "TableCaption";

// Interfaces para o componente de tabela de demandas
interface DemandaTableProps {
  demandas: Demanda[];
  onEdit?: (demanda: Demanda) => void;
  onView?: (demanda: Demanda) => void;
  onDelete?: (demanda: Demanda) => void;
  isLoading?: boolean;
  className?: string;
  maxHeight?: string;
  // Props para ordenação externa
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

// Função para obter o badge de status
const getStatusBadge = (status?: string) => {
  switch (status) {
    case 'EMATRIBUICAO':
      return <Badge variant="warning" size="sm">Em Atribuição</Badge>;
    case 'ATRIBUIDA':
      return <Badge variant="info" size="sm">Atribuída</Badge>;
    case 'EMPROGRESSO':
      return <Badge variant="info" size="sm">Em Progresso</Badge>;
    case 'AGUARDANDOAUTORIZACAO':
      return <Badge variant="warning" style="outline" size="sm">Aguardando Autorização</Badge>;
    case 'SOLUCIONADA':
      return <Badge variant="success" style="outline" size="sm">Solucionada</Badge>;
    case 'FINALIZADA':
      return <Badge variant="success" size="sm">Finalizada</Badge>;
    case 'DEVOLVIDA':
      return <Badge variant="error" style="outline" size="sm">Devolvida</Badge>;
    case 'ERRADA':
      return <Badge variant="error" size="sm">Errada</Badge>;
    case 'RECUSADA':
      return <Badge variant="error" size="sm">Recusada</Badge>;
    case 'EXCLUIDA':
      return <Badge variant="error" style="outline" size="sm">Excluída</Badge>;
    case 'REPROVADA':
      return <Badge variant="error" size="sm">Reprovada</Badge>;
    default:
      return <Badge variant="default" size="sm">Indefinido</Badge>;
  }
};

// Função para obter o badge de prioridade
const getPriorityBadge = (prioridade: string) => {
  switch (prioridade) {
    case 'Baixa':
      return <Badge variant="success" style="outline" size="sm">Baixa</Badge>;
    case 'Media':
      return <Badge variant="warning" style="outline" size="sm">Média</Badge>;
    case 'Alta':
      return <Badge variant="error" style="outline" size="sm">Alta</Badge>;
    case 'Urgente':
      return <Badge variant="error" size="sm">Urgente</Badge>;
    default:
      return <Badge variant="default" size="sm">{prioridade}</Badge>;
  }
};

// Componente principal da tabela de demandas
export const DemandaTable: React.FC<DemandaTableProps> = ({
  demandas,
  onEdit,
  onView,
  onDelete,
  isLoading = false,
  className,
  maxHeight,
  sorting: externalSorting,
  onSortingChange
}) => {
  // Remove avisos de variáveis não utilizadas (ações são opcionais)
  void onEdit; void onView; void onDelete;

  // Estado interno para ordenação (fallback)
  const [internalSorting, setInternalSorting] = useState<SortingState>([{ id: 'id', desc: true }]);
  
  // Usa ordenação externa se fornecida, senão usa interna
  const sorting = externalSorting || internalSorting;
  const setSorting = onSortingChange ? (updaterOrValue: any) => {
    const newSorting = typeof updaterOrValue === 'function' 
      ? updaterOrValue(externalSorting || [])
      : updaterOrValue;
    onSortingChange(newSorting);
  } : setInternalSorting;

  const columns = useMemo<ColumnDef<Demanda>[]>(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({ getValue }) => <span className="font-medium bg-transparent text-gray-900 dark:text-gray-100">#{getValue<number>()}</span>,
      size: 80,
    },
    {
      accessorKey: 'titulo',
      header: 'Título',
      cell: ({ row }) => {
        // Extrai texto puro da descrição removendo formatação HTML
        const descricaoLimpa = extractPlainText(row.original.descricao || '');
        const descricaoPreview = descricaoLimpa.length > 50
          ? `${descricaoLimpa.substring(0, 50)}...`
          : descricaoLimpa;
        
        // Verificar se é um Termo de Colaboração SEE
        const isTermoSEE = row.original.template_respostas?.respostas?.termo_colaboracao_see === true;
        
        return (
          <div className="max-w-xs">
            <div className="flex items-center gap-2">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={row.original.titulo}>
                {row.original.titulo}
              </div>
              {isTermoSEE && (
                <Badge variant="info" size="sm" className="flex-shrink-0">
                  Termo SEE
                </Badge>
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate" title={descricaoLimpa}>
              {descricaoPreview}
            </div>
          </div>
        );
      },
      size: 320,
      enableSorting: true,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => getStatusBadge(row.original.status),
      size: 140,
    },
    {
      accessorKey: 'prioridade',
      header: 'Prioridade',
      cell: ({ row }) => getPriorityBadge(row.original.prioridade),
      size: 120,
    },
    {
      id: 'processo',
      header: 'Processo',
      accessorFn: (d) => d.processo?.nome_processo ?? '',
      cell: ({ row }) => {
        if (!row.original.processo) {
          return <span className="text-gray-400 dark:text-gray-500">Não definido</span>;
        }

        const processName = row.original.processo?.nome_processo ?? 'Processo sem título';
        const sectorName = row.original.processo?.setor?.nome_setor ?? row.original.setor?.nome_setor ?? '';

        return (
          <div className="text-sm">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={processName}>
              {processName}
            </div>
            {sectorName && (
              <div className="text-gray-500 dark:text-gray-400 truncate text-xs" title={sectorName}>
                {sectorName}
              </div>
            )}
          </div>
        );
      },
      size: 200,
      enableSorting: true,
    },
    {
      id: 'demandante',
      header: 'Demandante',
      accessorFn: (d) => d.demandante?.name ?? '',
      cell: ({ row }) => (
        row.original.demandante ? (
          <div className="flex items-center space-x-3 bg-transparent">
            <div data-user-avatar>
              <UserAvatar
                user={row.original.demandante}
                size="sm"
                showPopup={true}
                showName={false}
                popupTrigger="hover"
                popupPosition="auto"
              />
            </div>
            <div className="text-sm min-w-0 flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{row.original.demandante.name}</div>
              <div className="text-gray-500 dark:text-gray-400 truncate">{row.original.demandante.email}</div>
            </div>
          </div>
        ) : <span className="text-gray-400 dark:text-gray-500">Não definido</span>
      ),
      size: 220,
    },
    {
      accessorKey: 'created_at',
      header: 'Criado em',
      cell: ({ row }) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{row.original.created_at ? formatDateSimple(row.original.created_at) : <span className="text-gray-400 dark:text-gray-500">Não definido</span>}</div>
      ),
      size: 140,
    },
    {
      accessorKey: 'sugestao_entrega',
      header: 'Prazo',
      cell: ({ row }) => {
        const atrasado = isPrazoAtrasado(row.original);

        const dataLabel = row.original.sugestao_entrega
        ? formatDateSimple(row.original.sugestao_entrega)
        : <span className='text-gray-400 dark:text-gray-500'>Não definido</span>;

        if (!atrasado) {
          return (
            <div className="ml-2.5 text-sm text-gray-900 dark:text-gray-100">
              {dataLabel}
            </div>
          );
        }

        return (
          <Badge variant="error" size="sm" style="outline">
            <div className="flex items-center gap-1 min-h-[24px]">
              <span className="font-semibold">{dataLabel}</span>
              <img src={iconLate} alt="Atrasado" className="size-5" />
            </div>
          </Badge>
        )

      },
      size: 140,
    },
    {
      id: 'setores',
      header: 'Setores',
      accessorFn: (d) => Array.isArray(d.setores) && d.setores.length > 0 ? d.setores.map(s => s.nome_setor).join(', ') : (d.setor?.nome_setor ?? ''),
      cell: ({ row }) => {
        const setores = Array.isArray(row.original.setores) && row.original.setores.length > 0
          ? row.original.setores
          : (row.original.setor ? [row.original.setor] : []);
        return setores.length > 0 ? (
          <div className="text-sm">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={setores.map(s => s.nome_setor).join(', ')}>
              {setores.map(s => s.nome_setor).join(', ')}
            </div>
          </div>
        ) : <span className="text-gray-400 dark:text-gray-500">Não definido</span>;
      },
      size: 140,
    },
    {
      id: 'demandado',
      header: 'Colaborador Atribuído',
      accessorFn: (d) => d.demandado?.name ?? '',
      cell: ({ row }) => (
        row.original.demandado ? (
          <div className="flex items-center space-x-3">
            <div data-user-avatar>
              <UserAvatar
                user={row.original.demandado}
                size="sm"
                showPopup={true}
                showName={false}
                popupTrigger="hover"
                popupPosition="auto"
              />
            </div>
            <div className="text-sm min-w-0 flex-1">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{row.original.demandado.name}</div>
              <div className="text-gray-500 dark:text-gray-400 truncate">{row.original.demandado.email}</div>
            </div>
          </div>
        ) : <span className="text-gray-400">Não atribuído</span>
      ),
      size: 220,
    },
    // A coluna de ações foi removida por solicitação
  ], [onEdit, onView, onDelete]);

  const table = useReactTable({
    data: demandas,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    // Sempre inclui getSortedRowModel, mas quando há ordenação externa os dados já vêm ordenados
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    // Desabilita ordenação interna quando há ordenação externa
    manualSorting: !!externalSorting,
  });

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="text-gray-500">Carregando demandas...</div>
        </div>
      </div>
    );
  }

  if (demandas.length === 0) {
    return (
      <div className="bg-white border border-gray-200 p-8">
        <div className="text-center">
          <div className="text-gray-500 mb-2">Nenhuma demanda encontrada</div>
          <div className="text-sm text-gray-400">Não há demandas cadastradas no momento.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full rounded-lg bg-white border border-gray-200", className)}>
      <Table className="table-fixed" {...(maxHeight && { maxHeight })}>
        <TableHeader className="sticky top-0 z-10 bg-gray-50 dark:bg-neutral-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() ? `${header.getSize()}px` : undefined }}>
                  {header.isPlaceholder ? null : header.column.getCanSort() ? (
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-2 select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <ChevronUp className="shrink-0 opacity-60" size={16} strokeWidth={2} aria-hidden="true" />,
                        desc: <ChevronDown className="shrink-0 opacity-60" size={16} strokeWidth={2} aria-hidden="true" />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const previsaoAtrasada = isPrevisaoAtrasada(row.original);
            return (
              <TableRow
                key={row.id}
                className={cn(
                  "cursor-pointer transition-colors group",
                  previsaoAtrasada
                    ? "bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/40"
                    : "hover:bg-blue-50 dark:hover:bg-blue-900/20"
                )}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('[data-user-avatar]') || target.closest('[data-user-popup]')) {
                    return;
                  }
                  onView?.(row.original);
                }}
                title="Clique para ver detalhes da demanda"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

// Exportar componentes base para uso individual se necessário
export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
};